/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/PublicHolidaysTab.tsx
 * PURPOSE: SC-033-TA-PUBLIC-HOLIDAYS — tab "Ngày nghỉ lễ" trong Cài đặt tổ chức.
 *          2 tab con: "Danh sách ngày lễ" (CRUD + filter/search/phân trang) và
 *          "Cấu hình" (chính sách ngày lễ trùng weekend + tham số Nghỉ bù).
 *          Màn NGUỒN — không có dữ liệu ở đây thì SC-024-EMP-LEAVE +
 *          SC-032-EMP-PUBLIC-HOLIDAYS luôn EMPTY.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-24
 * COMPLIANCE: SDLC 6.5.0 + BS-033-R01..R10 + Zero Mock Tolerance — nối API THẬT
 *             /api/v2/tenant-admin/public-holidays (+/config)
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs
 *
 * @business-context
 * - Domain: Tenant Administration — nguồn dữ liệu ngày lễ cho SC-024 (working_days)
 *   và SC-032 (employee read-only); cấu hình nghỉ bù cho Phase 3 (DD-0115/DD-0116)
 * - Impact: TENANT_ADMIN full quyền (thêm/sửa/xoá + đổi cấu hình); TENANT_OWNER
 *   chỉ xem (BS-033-R01 — ngược quy ước OWNER>ADMIN ở settings.py, đặc thù màn này)
 * - Stakeholders: Tenant Admins, Tenant Owners (read-only), @ba, @cpo, @cto
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Tab con của TenantSettings, theo pattern UnitTypesTab.tsx)
 * - Pattern: apiClient + getApiBase("v2") (theo UnitTypesTab), inline styles,
 *   token var(--nqh-*), toast tự ẩn 4s, useDebounce 300ms cho tìm kiếm
 * - Dependencies: React 18, apiClient, useAuthStore, useDebounce,
 *   backend/app/api/v2/tenant_admin/public_holidays.py
 *
 * @cross-references
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §2 SC-033-TA-PUBLIC-HOLIDAYS
 * - Business source: docs/01-planning/02-uat-len-lich-cham-cong/BS-024-PHAN-DOAN-D-NHAN-VIEN-NGHI-PHEP.md
 *   §BS-033-R01..R10, §BS-033-T01..T08
 * - API: backend/app/api/v2/tenant_admin/public_holidays.py
 * - Host: frontend/src/pages/tenant-admin/TenantSettings.tsx (TAB_CONFIG, key "public_holidays")
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../services/api/client";
import { API_BASE_URL } from "../../services/api/config";
import { useAuthStore } from "../../store/auth";
import { useDebounce } from "../../hooks/useDebounce";

/** Resolve API base URL from the canonical single source (services/api/config.ts). */
const getApiBase = (version: string) => `${API_BASE_URL}/api/${version}`;

// ---------------------------------------------------------------------------
// Types (mirror backend/app/api/v2/tenant_admin/public_holidays.py schemas)
// ---------------------------------------------------------------------------

interface PublicHoliday {
  id: string;
  name: string;
  start_date: string; // ISO yyyy-mm-dd
  end_date: string | null;
  is_recurring: boolean;
  description: string | null;
}

type StatusFilter = "" | "upcoming" | "past";

type HolidayFormData = {
  name: string;
  start_date: string;
  end_date: string; // "" = không nhập
  is_recurring: boolean;
  description: string;
};

const EMPTY_FORM: HolidayFormData = {
  name: "",
  start_date: "",
  end_date: "",
  is_recurring: false,
  description: "",
};

type WeekendOverlapPolicy = "next_workday" | "prev_workday" | "none";

interface HolidayConfig {
  weekend_overlap_policy: WeekendOverlapPolicy;
  compensatory_leave_enabled: boolean;
  compensatory_leave_expiry_months: number;
  compensatory_leave_max_days_per_year: number | null;
  compensatory_leave_auto_approve: boolean;
}

const PAGE_SIZE = 20; // BS-033-R06 — khớp PAGE_SIZE backend

// ---------------------------------------------------------------------------
// Style tokens — theo đúng convention UnitTypesTab.tsx (self-contained file)
// ---------------------------------------------------------------------------

const st = {
  card: {
    background: "var(--nqh-bg-surface, white)",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  } as React.CSSProperties,
  headTitle: {
    margin: "0 0 16px",
    fontSize: "16px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  innerTabBar: {
    display: "flex",
    gap: "4px",
    borderBottom: "1px solid var(--nqh-gray-200, #e5e7eb)",
    marginBottom: "20px",
  } as React.CSSProperties,
  innerTab: (active: boolean): React.CSSProperties => ({
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: active ? 600 : 500,
    color: active ? "var(--nqh-coffee-600, #b45309)" : "var(--nqh-gray-500, #6b7280)",
    background: "none",
    border: "none",
    borderBottom: active ? "2px solid var(--nqh-coffee-500, #d97706)" : "2px solid transparent",
    cursor: "pointer",
    marginBottom: "-1px",
  }),
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap" as const,
    marginBottom: "16px",
  } as React.CSSProperties,
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  select: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    fontSize: "14px",
    background: "white",
  } as React.CSSProperties,
  searchInput: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    fontSize: "14px",
    minWidth: "200px",
  } as React.CSSProperties,
  btnPrimary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "white",
    background: "var(--nqh-coffee-500, #d97706)",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  btnSecondary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "var(--nqh-gray-700, #374151)",
    background: "var(--nqh-gray-200, #e5e7eb)",
  } as React.CSSProperties,
  btnLink: {
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    background: "none",
    color: "var(--nqh-coffee-600, #b45309)",
  } as React.CSSProperties,
  btnLinkDanger: {
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    background: "none",
    color: "var(--nqh-chili, #dc2626)",
  } as React.CSSProperties,
  tableWrap: {
    overflowX: "auto" as const,
    border: "1px solid var(--nqh-gray-200, #e5e7eb)",
    borderRadius: "12px",
    background: "var(--nqh-bg-surface, white)",
  } as React.CSSProperties,
  table: {
    borderCollapse: "separate" as const,
    borderSpacing: 0,
    width: "100%",
    minWidth: "560px",
    fontSize: "14px",
  } as React.CSSProperties,
  th: {
    background: "var(--nqh-bg-sunken, #f9fafb)",
    color: "var(--nqh-gray-700, #374151)",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: ".02em",
    borderBottom: "1px solid var(--nqh-gray-200, #e5e7eb)",
    padding: "8px 12px",
    textAlign: "left" as const,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  td: {
    borderBottom: "1px solid var(--nqh-gray-100, #f3f4f6)",
    padding: "8px 12px",
    textAlign: "left" as const,
    verticalAlign: "middle" as const,
  } as React.CSSProperties,
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px",
    fontSize: "13px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "480px",
    width: "90%",
  } as React.CSSProperties,
  modalDialog: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "440px",
    width: "90%",
  } as React.CSSProperties,
  modalTitle: {
    margin: "0 0 16px",
    fontSize: "18px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  bannerError: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "var(--nqh-chili-050, #fef2f2)",
    border: "1px solid var(--nqh-chili-200, #fecaca)",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "16px",
    color: "var(--nqh-chili, #dc2626)",
    fontSize: "14px",
  } as React.CSSProperties,
  field: { marginBottom: "16px" } as React.CSSProperties,
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: 500,
    color: "var(--nqh-gray-700, #374151)",
    marginBottom: "6px",
  } as React.CSSProperties,
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  fieldError: {
    marginTop: "4px",
    fontSize: "12px",
    color: "var(--nqh-chili, #dc2626)",
  } as React.CSSProperties,
  fieldHint: {
    marginTop: "4px",
    fontSize: "12px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  checkboxLabel: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: "8px",
    cursor: "pointer",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
  } as React.CSSProperties,
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    minHeight: "28px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
  } as React.CSSProperties,
  modalFoot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "8px",
  } as React.CSSProperties,
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "nqh-ph-spin 0.8s linear infinite",
  } as React.CSSProperties,
  toast: {
    position: "fixed" as const,
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "var(--nqh-gray-900, #1f2937)",
    color: "white",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    zIndex: 1100,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  } as React.CSSProperties,
  skeletonRow: {
    height: "20px",
    borderRadius: "6px",
    background: "var(--nqh-gray-100, #f3f4f6)",
    margin: "12px",
  } as React.CSSProperties,
  emptyState: {
    textAlign: "center" as const,
    padding: "40px 20px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  configCard: {
    border: "1px solid var(--nqh-gray-200, #e5e7eb)",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "16px",
  } as React.CSSProperties,
  configCardTitle: {
    margin: "0 0 12px",
    fontSize: "15px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  configCardDesc: {
    margin: "0 0 12px",
    fontSize: "13px",
    color: "var(--nqh-gray-500, #6b7280)",
  } as React.CSSProperties,
  numberInputGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    maxWidth: "220px",
  } as React.CSSProperties,
  numberSuffix: {
    fontSize: "14px",
    color: "var(--nqh-gray-500, #6b7280)",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format d/m/yyyy — hoặc "start-end" nếu multi-day (wireframe SC-033 [5]). */
function formatHolidayDate(start: string, end: string | null): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };
  if (!end || end === start) return fmt(start);
  return `${fmt(start)}-${fmt(end)}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PublicHolidaysTab: React.FC = () => {
  const { userRole } = useAuthStore();
  // BS-033-R01: TENANT_OWNER chỉ xem (ẩn Thêm/Sửa/Xoá + Lưu cấu hình).
  const isReadOnly = userRole === "TENANT_OWNER";

  const [innerTab, setInnerTab] = useState<"list" | "config">("list");
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <div style={st.card}>
      <style>{`@keyframes nqh-ph-spin { to { transform: rotate(360deg); } }`}</style>
      <h3 style={st.headTitle}>Quản lý ngày nghỉ lễ</h3>

      <div style={st.innerTabBar} role="tablist" aria-label="Ngày nghỉ lễ">
        <button
          type="button"
          role="tab"
          aria-selected={innerTab === "list"}
          style={st.innerTab(innerTab === "list")}
          onClick={() => setInnerTab("list")}
        >
          Danh sách ngày lễ
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={innerTab === "config"}
          style={st.innerTab(innerTab === "config")}
          onClick={() => setInnerTab("config")}
        >
          Cấu hình
        </button>
      </div>

      {innerTab === "list" ? (
        <HolidayListPanel isReadOnly={isReadOnly} onToast={setToast} />
      ) : (
        <HolidayConfigPanel isReadOnly={isReadOnly} onToast={setToast} />
      )}

      {toast && (
        <div style={st.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default PublicHolidaysTab;

// ---------------------------------------------------------------------------
// Tab con 1 — Danh sách ngày lễ (BS-033-R03/R04/R05/R06)
// ---------------------------------------------------------------------------

const HolidayListPanel: React.FC<{
  isReadOnly: boolean;
  onToast: (msg: string) => void;
}> = ({ isReadOnly, onToast }) => {
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - 2 + i),
    [currentYear]
  );

  const [year, setYear] = useState<number>(currentYear);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<PublicHoliday[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchHolidays = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await apiClient.get("/tenant-admin/public-holidays", {
        baseURL: getApiBase("v2"),
        params: {
          year,
          status: statusFilter || undefined,
          search: debouncedSearch.trim() || undefined,
          page,
        },
      });
      setItems(Array.isArray(res.data?.holidays) ? res.data.holidays : []);
      setTotal(typeof res.data?.total === "number" ? res.data.total : 0);
    } catch {
      setLoadError("Không tải được danh sách ngày lễ · Thử lại");
    } finally {
      setLoading(false);
    }
  }, [year, statusFilter, debouncedSearch, page]);

  useEffect(() => {
    setLoading(true);
    fetchHolidays();
  }, [fetchHolidays]);

  // Đổi filter/search → quay về trang 1 (tránh trang rỗng do filter mới thu hẹp kết quả).
  useEffect(() => {
    setPage(1);
  }, [year, statusFilter, debouncedSearch]);

  // Modal Thêm/Sửa
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PublicHoliday | null>(null);
  const [form, setForm] = useState<HolidayFormData>(EMPTY_FORM);
  const [nameErr, setNameErr] = useState(false);
  const [startErr, setStartErr] = useState(false);
  const [dateOrderErr, setDateOrderErr] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Modal xác nhận xoá
  const [deleteTarget, setDeleteTarget] = useState<PublicHoliday | null>(null);
  // C6 guard (idempotency/double-action): khoá đúng dòng đang có request DELETE bay.
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setNameErr(false); setStartErr(false); setDateOrderErr(false); setBanner(null);
    setShowModal(true);
  };

  const openEdit = (item: PublicHoliday) => {
    setEditing(item);
    setForm({
      name: item.name,
      start_date: item.start_date,
      end_date: item.end_date || "",
      is_recurring: item.is_recurring,
      description: item.description || "",
    });
    setNameErr(false); setStartErr(false); setDateOrderErr(false); setBanner(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    const badName = !form.name.trim();
    const badStart = !form.start_date;
    const badOrder = !!(form.end_date && form.start_date && form.end_date < form.start_date);
    setNameErr(badName); setStartErr(badStart); setDateOrderErr(badOrder); setBanner(null);
    if (badName || badStart || badOrder) return;

    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date || null,
        is_recurring: form.is_recurring,
        description: form.description.trim() || null,
      };
      if (editing) {
        await apiClient.put(`/tenant-admin/public-holidays/${editing.id}`, body, { baseURL: getApiBase("v2") });
      } else {
        await apiClient.post("/tenant-admin/public-holidays", body, { baseURL: getApiBase("v2") });
      }
      setShowModal(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      await fetchHolidays();
      onToast(editing ? "Đã lưu ngày nghỉ lễ" : "Đã thêm ngày nghỉ lễ");
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setBanner(typeof msg === "string" ? msg : "Lưu ngày nghỉ lễ thất bại. Vui lòng thử lại.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deletingId === deleteTarget.id) return; // C6 guard
    setDeletingId(deleteTarget.id);
    try {
      await apiClient.delete(`/tenant-admin/public-holidays/${deleteTarget.id}`, { baseURL: getApiBase("v2") });
      setDeleteTarget(null);
      await fetchHolidays();
      onToast("Đã xoá ngày nghỉ lễ");
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      onToast(typeof msg === "string" ? msg : "Xoá ngày nghỉ lễ thất bại. Vui lòng thử lại.");
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  if (loading) {
    return (
      <div data-testid="public-holidays-skeleton">
        <div style={st.tableWrap}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={st.skeletonRow} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* [1][2][3][4] Toolbar — filter năm/trạng thái/tìm kiếm + Thêm */}
      <div style={st.toolbar}>
        <div style={st.toolbarLeft}>
          <select
            style={st.select}
            aria-label="Năm"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            style={st.select}
            aria-label="Trạng thái"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="">Tất cả</option>
            <option value="upcoming">Sắp diễn ra</option>
            <option value="past">Đã qua</option>
          </select>
          <input
            type="text"
            style={st.searchInput}
            placeholder="🔎 Tìm theo tên..."
            aria-label="Tìm kiếm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {!isReadOnly && (
          <button type="button" style={st.btnPrimary} onClick={openCreate} data-testid="btn-add-holiday">
            + Thêm ngày lễ
          </button>
        )}
      </div>

      {loadError && (
        <div style={{ ...st.bannerError, justifyContent: "space-between" }} role="alert">
          <span>{loadError}</span>
          <button type="button" style={st.btnSecondary} onClick={() => { setLoading(true); fetchHolidays(); }}>
            Thử lại
          </button>
        </div>
      )}

      {/* [5] Bảng danh sách ngày lễ */}
      {!loadError && (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th} scope="col">Ngày</th>
                <th style={st.th} scope="col">Tên ngày lễ</th>
                {!isReadOnly && (
                  <th style={{ ...st.th, textAlign: "right" as const }} scope="col">Thao tác</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={st.td}>{formatHolidayDate(item.start_date, item.end_date)}</td>
                  <td style={st.td}>{item.name}</td>
                  {!isReadOnly && (
                    <td style={{ ...st.td, textAlign: "right" as const, whiteSpace: "nowrap" as const }}>
                      <button type="button" style={st.btnLink} onClick={() => openEdit(item)}>
                        Sửa
                      </button>
                      <button
                        type="button"
                        style={st.btnLinkDanger}
                        disabled={deletingId === item.id}
                        onClick={() => setDeleteTarget(item)}
                      >
                        Xóa
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {items.length === 0 && (
            <div style={st.emptyState}>
              <p style={{ margin: "0 0 8px", fontSize: "14px" }}>
                📭 Chưa có ngày lễ nào cho năm {year}
              </p>
              {!isReadOnly && <p style={{ margin: 0, fontSize: "13px" }}>Bấm "+ Thêm ngày lễ" để bắt đầu</p>}
            </div>
          )}

          {items.length > 0 && (
            <div style={st.pagination}>
              <span>Hiển thị {rangeStart}-{rangeEnd} / {total} ngày lễ</span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  style={st.btnSecondary}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Trước
                </button>
                <button
                  type="button"
                  style={st.btnSecondary}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Sau ›
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* [7]-[11] Modal Thêm/Sửa ngày lễ */}
      {showModal && (
        <div style={st.overlay} onClick={() => !saving && setShowModal(false)}>
          <div
            style={st.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="holidayModalTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={st.modalTitle} id="holidayModalTitle">
              {editing ? "Sửa ngày nghỉ lễ" : "Thêm ngày nghỉ lễ"}
            </h2>

            {banner && (
              <div style={st.bannerError} role="alert" data-testid="holiday-banner">
                ⚠ {banner}
              </div>
            )}

            <div style={st.field}>
              <label style={st.label} htmlFor="fHolidayName">Tên ngày lễ *</label>
              <input
                id="fHolidayName"
                style={{ ...st.input, borderColor: nameErr ? "var(--nqh-chili, #dc2626)" : undefined }}
                type="text"
                maxLength={255}
                value={form.name}
                disabled={saving}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {nameErr && <div style={st.fieldError}>Nhập tên ngày lễ</div>}
            </div>

            <div style={st.field}>
              <label style={st.label} htmlFor="fHolidayStart">Ngày bắt đầu *</label>
              <input
                id="fHolidayStart"
                style={{ ...st.input, borderColor: startErr || dateOrderErr ? "var(--nqh-chili, #dc2626)" : undefined }}
                type="date"
                value={form.start_date}
                disabled={saving}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
              {startErr && <div style={st.fieldError}>Chọn ngày bắt đầu</div>}
            </div>

            <div style={st.field}>
              <label style={st.label} htmlFor="fHolidayEnd">Ngày kết thúc (nếu lễ nhiều ngày)</label>
              <input
                id="fHolidayEnd"
                style={{ ...st.input, borderColor: dateOrderErr ? "var(--nqh-chili, #dc2626)" : undefined }}
                type="date"
                value={form.end_date}
                disabled={saving}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
              {dateOrderErr && (
                <div style={st.fieldError}>Ngày kết thúc phải sau hoặc bằng ngày bắt đầu.</div>
              )}
            </div>

            <div style={st.field}>
              <label style={st.checkboxLabel} htmlFor="fHolidayRecurring">
                <input
                  id="fHolidayRecurring"
                  type="checkbox"
                  checked={form.is_recurring}
                  disabled={saving}
                  onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                />
                <span>Lặp lại hàng năm (cùng ngày/tháng, tạo tự động cho các năm sau)</span>
              </label>
              <div style={st.fieldHint}>Chỉ nên dùng cho ngày lễ có ngày/tháng cố định hàng năm</div>
            </div>

            <div style={st.field}>
              <label style={st.label} htmlFor="fHolidayDesc">Mô tả (không bắt buộc)</label>
              <textarea
                id="fHolidayDesc"
                style={{ ...st.input, minHeight: "72px", resize: "vertical" as const }}
                value={form.description}
                disabled={saving}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div style={st.modalFoot}>
              <button type="button" style={st.btnSecondary} disabled={saving} onClick={() => setShowModal(false)}>
                Hủy
              </button>
              <button type="button" style={st.btnPrimary} disabled={saving} onClick={handleSave}>
                {saving && <span style={st.spinner} aria-hidden="true" />}
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận xoá (BS-033-R04) */}
      {deleteTarget && (
        <div style={st.overlay} onClick={() => setDeleteTarget(null)}>
          <div
            style={st.modalDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteHolidayTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={st.modalTitle} id="deleteHolidayTitle">Xoá ngày nghỉ lễ?</h2>
            <p style={{ margin: "0 0 16px", fontSize: "14px", color: "var(--nqh-gray-700, #374151)" }}>
              Bạn có chắc muốn xoá "{deleteTarget.name}" ({formatHolidayDate(deleteTarget.start_date, deleteTarget.end_date)})?
              Hành động này ảnh hưởng tới cách tính số ngày phép của mọi đơn nghỉ phép TƯƠNG LAI
              (không hồi tố đơn đã tạo).
            </p>
            <div style={st.modalFoot}>
              <button type="button" style={st.btnSecondary} disabled={!!deletingId} onClick={() => setDeleteTarget(null)}>
                Hủy
              </button>
              <button
                type="button"
                style={{ ...st.btnPrimary, background: "var(--nqh-chili, #dc2626)" }}
                disabled={!!deletingId}
                onClick={handleDelete}
              >
                {deletingId && <span style={st.spinner} aria-hidden="true" />}
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab con 2 — Cấu hình (BS-033-R07/R08/R09)
// ---------------------------------------------------------------------------

const CONFIG_DEFAULTS: HolidayConfig = {
  weekend_overlap_policy: "none",
  compensatory_leave_enabled: true,
  compensatory_leave_expiry_months: 12,
  compensatory_leave_max_days_per_year: null,
  compensatory_leave_auto_approve: true,
};

const HolidayConfigPanel: React.FC<{
  isReadOnly: boolean;
  onToast: (msg: string) => void;
}> = ({ isReadOnly, onToast }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<HolidayConfig>(CONFIG_DEFAULTS);
  const [form, setForm] = useState<HolidayConfig>(CONFIG_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await apiClient.get("/tenant-admin/public-holidays/config", { baseURL: getApiBase("v2") });
      const cfg: HolidayConfig = {
        weekend_overlap_policy: res.data.weekend_overlap_policy,
        compensatory_leave_enabled: res.data.compensatory_leave_enabled,
        compensatory_leave_expiry_months: res.data.compensatory_leave_expiry_months,
        compensatory_leave_max_days_per_year: res.data.compensatory_leave_max_days_per_year ?? null,
        compensatory_leave_auto_approve: res.data.compensatory_leave_auto_approve,
      };
      setSaved(cfg);
      setForm(cfg);
    } catch {
      setLoadError("Không tải được cấu hình · Thử lại");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const handleSave = async () => {
    setBanner(null);
    setSaving(true);
    try {
      const res = await apiClient.put("/tenant-admin/public-holidays/config", form, { baseURL: getApiBase("v2") });
      const cfg: HolidayConfig = {
        weekend_overlap_policy: res.data.weekend_overlap_policy,
        compensatory_leave_enabled: res.data.compensatory_leave_enabled,
        compensatory_leave_expiry_months: res.data.compensatory_leave_expiry_months,
        compensatory_leave_max_days_per_year: res.data.compensatory_leave_max_days_per_year ?? null,
        compensatory_leave_auto_approve: res.data.compensatory_leave_auto_approve,
      };
      setSaved(cfg);
      setForm(cfg);
      onToast("Đã lưu cấu hình");
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setBanner(typeof msg === "string" ? msg : "Lưu cấu hình thất bại. Vui lòng thử lại.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="holiday-config-skeleton">
        <div style={st.skeletonRow} />
        <div style={st.skeletonRow} />
        <div style={st.skeletonRow} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ ...st.bannerError, justifyContent: "space-between" }} role="alert">
        <span>{loadError}</span>
        <button type="button" style={st.btnSecondary} onClick={() => { setLoading(true); loadConfig(); }}>
          Thử lại
        </button>
      </div>
    );
  }

  const compFieldsDisabled = isReadOnly || saving || !form.compensatory_leave_enabled;

  return (
    <div>
      {banner && (
        <div style={st.bannerError} role="alert" data-testid="config-banner">
          ⚠ {banner}
        </div>
      )}

      {/* [12] Ngày lễ trùng ngày nghỉ hàng tuần */}
      <div style={st.configCard}>
        <h4 style={st.configCardTitle}>Ngày lễ trùng ngày nghỉ hàng tuần</h4>
        <p style={st.configCardDesc}>Khi ngày lễ rơi vào Thứ 7 hoặc Chủ nhật:</p>
        <div role="radiogroup" aria-label="Ngày lễ trùng ngày nghỉ hàng tuần">
          <label style={st.radioLabel}>
            <input
              type="radio"
              name="weekendOverlapPolicy"
              checked={form.weekend_overlap_policy === "next_workday"}
              disabled={isReadOnly || saving}
              onChange={() => setForm({ ...form, weekend_overlap_policy: "next_workday" })}
            />
            Tự động nghỉ bù vào ngày làm việc tiếp theo
          </label>
          <label style={st.radioLabel}>
            <input
              type="radio"
              name="weekendOverlapPolicy"
              checked={form.weekend_overlap_policy === "prev_workday"}
              disabled={isReadOnly || saving}
              onChange={() => setForm({ ...form, weekend_overlap_policy: "prev_workday" })}
            />
            Tự động nghỉ bù vào ngày làm việc trước đó
          </label>
          <label style={st.radioLabel}>
            <input
              type="radio"
              name="weekendOverlapPolicy"
              checked={form.weekend_overlap_policy === "none"}
              disabled={isReadOnly || saving}
              onChange={() => setForm({ ...form, weekend_overlap_policy: "none" })}
            />
            Không nghỉ bù (chỉ hưởng 300% lương khi đi làm)
          </label>
        </div>
      </div>

      {/* [13]-[17] Nghỉ bù cho nhân viên làm việc ngày lễ */}
      <div style={st.configCard}>
        <h4 style={st.configCardTitle}>Nghỉ bù cho nhân viên làm việc ngày lễ</h4>

        <div style={st.field}>
          <div role="radiogroup" aria-label="Bật/Tắt nghỉ bù" style={{ display: "flex", gap: "16px" }}>
            <label style={st.radioLabel}>
              <input
                type="radio"
                name="compensatoryEnabled"
                checked={form.compensatory_leave_enabled}
                disabled={isReadOnly || saving}
                onChange={() => setForm({ ...form, compensatory_leave_enabled: true })}
              />
              Bật
            </label>
            <label style={st.radioLabel}>
              <input
                type="radio"
                name="compensatoryEnabled"
                checked={!form.compensatory_leave_enabled}
                disabled={isReadOnly || saving}
                onChange={() => setForm({ ...form, compensatory_leave_enabled: false })}
              />
              Tắt
            </label>
          </div>
        </div>

        <div style={st.field}>
          <label style={st.label} htmlFor="fExpiryMonths">Thời hạn sử dụng nghỉ bù</label>
          <div style={st.numberInputGroup}>
            <input
              id="fExpiryMonths"
              type="number"
              min={1}
              style={st.input}
              value={form.compensatory_leave_expiry_months}
              disabled={compFieldsDisabled}
              onChange={(e) => setForm({ ...form, compensatory_leave_expiry_months: Math.max(1, Number(e.target.value) || 1) })}
            />
            <span style={st.numberSuffix}>tháng</span>
          </div>
        </div>

        <div style={st.field}>
          <label style={st.label} htmlFor="fMaxDaysPerYear">
            Giới hạn số ngày nghỉ bù tối đa/năm (không bắt buộc)
          </label>
          <div style={st.numberInputGroup}>
            <input
              id="fMaxDaysPerYear"
              type="number"
              min={0}
              style={st.input}
              value={form.compensatory_leave_max_days_per_year ?? ""}
              disabled={compFieldsDisabled}
              onChange={(e) => {
                const v = e.target.value;
                setForm({
                  ...form,
                  compensatory_leave_max_days_per_year: v === "" ? null : Math.max(0, Number(v)),
                });
              }}
            />
            <span style={st.numberSuffix}>ngày</span>
          </div>
        </div>

        <div style={st.field}>
          <div role="radiogroup" aria-label="Duyệt đơn nghỉ bù">
            <label style={st.radioLabel}>
              <input
                type="radio"
                name="compensatoryAutoApprove"
                checked={form.compensatory_leave_auto_approve}
                disabled={compFieldsDisabled}
                onChange={() => setForm({ ...form, compensatory_leave_auto_approve: true })}
              />
              Tự động duyệt đơn nghỉ bù
            </label>
            <label style={st.radioLabel}>
              <input
                type="radio"
                name="compensatoryAutoApprove"
                checked={!form.compensatory_leave_auto_approve}
                disabled={compFieldsDisabled}
                onChange={() => setForm({ ...form, compensatory_leave_auto_approve: false })}
              />
              Cần Quản lý duyệt như đơn nghỉ phép thường
            </label>
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            style={st.btnPrimary}
            disabled={!dirty || saving}
            onClick={handleSave}
            data-testid="btn-save-config"
          >
            {saving && <span style={st.spinner} aria-hidden="true" />}
            Lưu cấu hình
          </button>
        </div>
      )}
    </div>
  );
};
